// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { createFilterValue, editFilterValue, getFilterValues } from '../../feature/actionCreators';
import { categoriesSelector } from '../../feature/selectors';

// Components
import { IFormInstance, showSuccessToast } from '@/components';
import { Skeleton } from '@/ui';
import { CreateFilterValueForm } from '../../components';
import { StyledPageWrapper } from './CreateFilterValueContainerStyles';

// Models
import { IFilterValue } from '@/models/filters.model';

// Helpers
import { TCreateFilterValue } from '../../helpers/types';

interface ICreateFilterValueContainer {
  form: IFormInstance;
  filter_id: string;
  currentFilterValue?: IFilterValue | null;
  onFormSubmit: () => void;
}

const CreateFilterValueContainer = ({
  form,
  onFormSubmit,
  filter_id,
  currentFilterValue,
}: ICreateFilterValueContainer) => {
  const dispatch = useAppDispatch();
  const { isLoadingFilterDetails } = useAppState(categoriesSelector);
  const { t } = useTranslation(['categories']);

  const handleSubmit = useCallback(
    async (values: TCreateFilterValue) => {
      try {
        const updateAction = currentFilterValue?.id ? editFilterValue : createFilterValue;
        await dispatch(updateAction({ data: values, filter_id, id: currentFilterValue?.id })).unwrap();
        await dispatch(getFilterValues({ page: 1, filter_id })).unwrap();
        onFormSubmit();
        showSuccessToast({ message: t('data_saved') });
      } catch (error) {}
    },
    [filter_id, currentFilterValue],
  );

  useEffect(() => {
    if (currentFilterValue) {
      form.setFieldsValue(currentFilterValue);
    } else {
      form.resetFields();
    }
  }, [currentFilterValue]);

  if (isLoadingFilterDetails) return <Skeleton />;

  return (
    <StyledPageWrapper>
      <CreateFilterValueForm
        handleSubmit={handleSubmit}
        isLoading={isLoadingFilterDetails}
        form={form}
        initialValues={currentFilterValue}
      />
    </StyledPageWrapper>
  );
};

export default CreateFilterValueContainer;
